// Disclosure eşleme tablosu (TODO-KPI-08): standart kodu | gösterge | kategori | eşleme | durum | değer.
// Satıra tıkla → Veri Girişi'nde o göstergeye git (?highlight=<code>).
import { useMemo } from "react";
import { formatKpiNumber, type KpiDisclosureRow, type KpiResolvedIndicator } from "@mahir/shared";
import { useMediaQuery } from "../../../hooks/use-media-query";
import { useTreeCollapsed } from "../../../hooks/use-tree-collapsed";
import { buildTreeList, treeIndent, type TreeListRow } from "../../../lib/kpi-tree-list";
import { KpiTreeBandRow, KpiTreeToolbar } from "../KpiTreeBandRow";
import { TreeLabelStack } from "./TreeLabelStack";
import {
  disclosureStatusLabel,
  disclosureStatusTone,
  KPI_TARGETS_COPY,
  mappingLabel,
} from "../../../lib/kpi-targets-ui";

const C = KPI_TARGETS_COPY.disclosure;

const th: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-3)",
  borderBottom: "1px solid var(--color-border)",
  textAlign: "left",
  whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--color-border)",
  fontSize: "0.85rem",
  color: "var(--color-text)",
  verticalAlign: "top",
};

export function StatusPill({ status }: { status: KpiDisclosureRow["status"] }): JSX.Element {
  const tone = disclosureStatusTone(status);
  const map = {
    ok: {
      bg: "color-mix(in srgb, var(--color-primary) 10%, white)",
      fg: "var(--color-primary)",
      border: "transparent",
    },
    wait: {
      bg: "var(--color-warning-bg)",
      fg: "var(--color-warning-text)",
      border: "var(--color-warning-border)",
    },
    xxx: { bg: "var(--color-bg)", fg: "var(--color-text-2)", border: "var(--color-border)" },
    muted: { bg: "transparent", fg: "var(--color-text-3)", border: "transparent" },
  }[tone];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: "0.66rem",
        fontWeight: 700,
        background: map.bg,
        color: map.fg,
        border: `1px solid ${map.border}`,
        textTransform: "uppercase",
      }}
    >
      {disclosureStatusLabel(status)}
    </span>
  );
}

export function DisclosureTable({
  rows,
  onOpen,
  emptyText,
  tree,
}: {
  rows: KpiDisclosureRow[];
  onOpen: (code: string) => void;
  emptyText: string;
  /** T7 — kategori ağacı: `allRows` tüm eşlemeler (bant sayaçları), `indicators` set göstergeleri */
  tree?: {
    indicators: KpiResolvedIndicator[];
    allRows: KpiDisclosureRow[];
    filtered: boolean;
    screen?: string;
    initialCollapsed?: string[];
  };
}): JSX.Element {
  const tc = useTreeCollapsed(
    tree?.screen ?? "disclosure",
    tree?.indicators ?? [],
    tree?.initialCollapsed,
  );
  const narrow = useMediaQuery("(max-width: 1024px)");
  const list = useMemo<TreeListRow<KpiDisclosureRow>[]>(() => {
    if (!tree)
      return rows.map((r) => ({ kind: "item", data: r, indicatorId: r.indicatorId, depth: 0 }));
    const visible = new Set(rows);
    return buildTreeList(
      tree.indicators,
      tree.allRows.map((r) => ({ indicatorId: r.indicatorId, matches: visible.has(r), data: r })),
      { collapsed: tc.collapsed, filtered: tree.filtered },
    );
  }, [tree, rows, tc.collapsed]);
  return (
    <div className="kpi-scroll-x">
      {list.some((r) => r.kind === "band") ? (
        <KpiTreeToolbar onCollapseAll={tc.collapseAll} onExpandAll={tc.expandAll} />
      ) : null}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>{C.columns.code}</th>
            <th style={th}>{C.columns.indicator}</th>
            <th style={th}>{C.columns.category}</th>
            <th style={th}>{C.columns.mapping}</th>
            <th style={th}>{C.columns.status}</th>
            <th style={{ ...th, textAlign: "right" }}>{C.columns.value}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                style={{ ...td, textAlign: "center", color: "var(--color-text-3)", padding: 28 }}
              >
                {emptyText}
              </td>
            </tr>
          ) : null}
          {list.map((entry) => {
            if (entry.kind === "band") {
              return (
                <KpiTreeBandRow
                  key={`band-${entry.indicator.id}`}
                  row={entry}
                  colSpan={6}
                  narrow={narrow}
                  filtered={tree?.filtered ?? false}
                  onToggle={tc.setBand}
                />
              );
            }
            const r = entry.data;
            return (
              <tr key={`${r.metricId}-${r.standardCode}`} data-code={r.standardCode}>
                <td
                  style={{
                    ...td,
                    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.standard} {r.standardCode}
                </td>
                <td style={{ ...td, textAlign: "left" }}>
                  <TreeLabelStack
                    label={r.label}
                    indent={treeIndent(entry.depth, narrow)}
                    onOpen={() => onOpen(r.code)}
                    title={C.rowHint}
                    sub={r.note}
                  />
                </td>
                <td style={{ ...td, color: "var(--color-text-2)", fontSize: "0.8rem" }}>
                  {r.parentLabel ?? "—"}
                </td>
                <td style={{ ...td, color: "var(--color-text-2)", fontSize: "0.8rem" }}>
                  {mappingLabel(r.mappingType)}
                </td>
                <td style={td}>
                  <StatusPill status={r.status} />
                </td>
                <td
                  style={{
                    ...td,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {typeof r.value === "number"
                    ? formatKpiNumber(r.value, {
                        percent: r.valueType === "percent",
                        unit: r.valueType === "percent" ? undefined : (r.unit ?? undefined),
                      })
                    : (r.text ?? "—")}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
